import { Chart } from "react-google-charts";
import { ThemeContext } from "../../../../theme/ThemeContext";
import { useContext, useState, useEffect, useRef } from "react";
import DashboardService from "../../../../services/DashboardService";
import AuthContext from "../../../../auth/AuthContext";
import { Skeleton } from "primereact/skeleton";
import { useResizeDetector } from "react-resize-detector"; 

const colorPalette = [
  "#1e88e5", "#fb8c00", "#1A5088", "#e53935", "#43a047", "#8e24aa",
  "#f4511e", "#039be5", "#7cb342", "#d81b60", "#00897b", "#5e35b1",
  "#c0ca33", "#6d4c41", "#00acc1", "#ff8f00", "#3949ab", "#e91e63",
  "#00bfa5", "#4dd0e1", "#ff7043", "#26c6da", "#ab47bc", "#66bb6a",
  "#ef5350", "#ec407a", "#42a5f5", "#ffa726", "#26a69a", "#8d6e63"
];

const firmTypes = ["Institute", "Organization", "Government"];

export default function CoursesStatusChart() {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const authContext = useContext(AuthContext);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("Institute");

  const [chartKey, setChartKey] = useState(0); 

  const { ref, width, height } = useResizeDetector();

  const selectRef = useRef(null);

  useEffect(() => {
    if (width && height) {
      setChartKey(prev => prev + 1);
    }
  }, [width, height]);

useEffect(() => {
    const controller = new AbortController(); 

    const fetchCourses = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await DashboardService.GetCourseChart(authContext, controller.signal );

        if (controller.signal.aborted) return;

        if (!response) return;
        if (response?.isSuccess) {
          setCourses(response.data);
        } else {
          setError("Failed to fetch courses");
        }
      } catch (err) {
        if (err.name === "AbortError") return; 
        setError("Failed to fetch courses");
      } finally {
        if (!controller.signal.aborted) setLoading(false); 
      }
    };

    fetchCourses();

    return () => controller.abort();
  }, [authContext]);

  const filteredCourses = courses.filter(
    (course) => course.firmType === selectedCategory
  );

  const totalCourses = filteredCourses.length;

  const chartData = [
    ["Course", "Users"],
    ...filteredCourses.map((c) => [c.courseName, c.count]),
  ];


  const getDynamicColors = (count) => {
  if (count === 0) return [colorPalette[0]];
  return Array.from({ length: count }, (_, i) => colorPalette[i % colorPalette.length]);
};

const dynamicColors = getDynamicColors(filteredCourses.length);

const options = {
  backgroundColor: "transparent",
  pieHole: 0.4,
  is3D: true, 
  pieStartAngle: 100,
  sliceVisibilityThreshold: 0,

  chartArea: {
    width: "75%",   
    height: "80%", 
    left: "12%",   
    top: "5%",
  },

  legend: {
    position: "bottom",
    alignment: "center",
    textStyle: {
      fontSize: 11,
      color: isDark ? "#eaeaea" : "#000",
      fontName: "Poppins",
    },
  },
        tooltip: { 
        isHtml: false, 
        textStyle: {
          fontSize: 10,
          fontName: "Poppins",
        },
        showColorCode: true,
      },
  colors: dynamicColors,
};

    const handleSelectClick = (e) => {
      e.stopPropagation();
    };

  return (
    <div
      className="charts"
      style={{
        width: "100%",
        height: "357px", 
        padding: "14px",
        borderRadius: "12px",
      }}
    >
          {loading ? (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "0px",
              }}
            >
              <Skeleton width="120px" height="20px" />
              <Skeleton width="90px" height="24px" borderRadius="6px" />
            </div>
            <Skeleton width="100px" height="15px" className="mt-1" />
            <div
              style={{
                marginTop: "20px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Skeleton shape="circle" size="200px" />
            </div>
          </>
        ) : error ? (
          <div style={{ padding: "14px", color: "red", textAlign: "center" }}>
            {error}
          </div>
        ) : (
        <>
          {/* Header */}
          <div className="d-flex justify-content-between">
            <div>
              <div style={{ fontSize: 16 }}>Course</div>
                <p
                  style={{
                    fontSize: "13px",
                    color: isDark ? "#eaeaea" : "#6c757d",
                  }}
                >
                Total Courses: <b>{totalCourses}</b>
              </p>
            </div>
             <div
                style={{
                  gap: "4px",
                  zIndex: 10,
                }}
              >
                <select
                  ref={selectRef}
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  onClick={handleSelectClick}
                  onMouseDown={handleSelectClick}
                  style={{
                    fontSize: "12px",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    backgroundColor: isDark ? "#2d2d2d" : "#fff",
                    color: isDark ? "#eaeaea" : "#000",
                  }}
                >

              {firmTypes.map((firm) => (
                <option key={firm} value={firm}>
                  {firm}
                </option>
              ))}
            </select>
             </div>
          </div>

          {/* Chart */}
          <div
            ref={ref} 
            style={{
              width: "100%",
              height: "calc(100% - 80px)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              overflow: "hidden",
            }}
          >
             {filteredCourses.length > 0 ? (
            <Chart
              key={chartKey} 
              chartType="PieChart"
              data={chartData}
              options={options}
              width="100%"
              height="100%"
            />
              ) : (
                <div
                  style={{
                    height: "330px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: isDark ? "#aaa" : "#666",
                    fontSize: "14px",
                  }}
                >
                  No courses available for {selectedCategory}
                </div>
              )}
          </div>
        </>
      )}
    </div>
  );
}